import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../services/api";
import { createDevConsole } from "../../../utils/devLogger";

const console = createDevConsole();

const BasicConfiguration = ({ data, onChange, onNext, onBack, reportType }) => {
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Relationship reports don't need entity_type here - they configure it in the configuration step
  const showEntityType = reportType !== "relationship";

  useEffect(() => {
    if (showEntityType) {
      loadEntityTypes();
    } else {
      setLoading(false);
    }
  }, [showEntityType]);

  const loadEntityTypes = async () => {
    try {
      const response = await apiFetch("/api/entity-types");
      if (response.ok) {
        const result = await response.json();
        // The API returns {types: [...], total: number}
        const types =
          result.types ||
          result.entity_types ||
          (Array.isArray(result) ? result : []);
        setEntityTypes(types);
        console.log("Loaded entity types:", types);
      } else {
        console.error(
          "Failed to load entity types:",
          response.status,
          response.statusText
        );
        const errorText = await response.text();
        console.error("Error response:", errorText);
      }
    } catch (err) {
      console.error("Error loading entity types:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    onChange({
      [field]: value,
    });
  };

  const handleTagsChange = (tagsString) => {
    const tags = tagsString
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    onChange({ tags });
  };

  // For relationship reports, we don't require entity_type since it's set in configuration
  const canProceed =
    data.name &&
    data.name.trim().length > 0 &&
    (reportType === "relationship" ||
      (data.entity_type && data.entity_type.length > 0));

  // Dynamic placeholders based on report type
  const getPlaceholders = () => {
    switch (reportType) {
      case "roadmap":
        return {
          name: "e.g., Application Roadmap, Technology Roadmap, Platform Evolution",
          description: "Optional description of what this roadmap shows (e.g., timeline, lifecycle status, dependencies)...",
          tags: "e.g., roadmap, applications, planning, timeline",
        };
      case "data-quality":
        return {
          name: "e.g., Application Data Quality, Entity Quality Assessment, Data Completeness Report",
          description: "Optional description of what this data quality report analyzes (e.g., missing attributes, completeness metrics)...",
          tags: "e.g., data-quality, analysis, completeness, assessment",
        };
      case "relationship":
        return {
          name: "e.g., Application Dependencies, System Impact Analysis, Integration Mapping",
          description: "Optional description of what this relationship analysis covers (e.g., dependencies, impacts, connections)...",
          tags: "e.g., relationships, dependencies, impact-analysis, integration",
        };
      default:
        return {
          name: "e.g., Custom Report",
          description: "Optional description of what this report shows...",
          tags: "e.g., report, analysis",
        };
    }
  };

  const placeholders = getPlaceholders();

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">
        Basic Information
      </h2>
      <p className="text-gray-400 mb-6">
        Provide basic details about your report
      </p>

      <div className="space-y-4">
        {/* Report Name */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Report Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={placeholders.name}
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            required
          />
        </div>

        {/* Entity Type - not shown for relationship reports since they configure it in the configuration step */}
        {showEntityType && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Entity Type <span className="text-red-400">*</span>
            </label>
            {loading ? (
              <div className="text-gray-400">Loading entity types...</div>
            ) : (
              <select
                value={data.entity_type}
                onChange={(e) => handleChange("entity_type", e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                required
              >
                <option value="">Select entity type...</option>
                {entityTypes.map((type, index) => (
                  <option
                    key={type.id || type.value || index}
                    value={type.value || type.name}
                  >
                    {type.label || type.display_name || type.value || type.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={data.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder={placeholders.description}
            rows={4}
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Category
          </label>
          <input
            type="text"
            value={data.category}
            onChange={(e) => handleChange("category", e.target.value)}
            placeholder="e.g., Planning, Analysis, Compliance"
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={data.tags.join(", ")}
            onChange={(e) => handleTagsChange(e.target.value)}
            placeholder={placeholders.tags}
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Separate multiple tags with commas
          </p>
        </div>

        {/* Share with Tenant */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_shared"
            checked={data.is_shared}
            onChange={(e) => handleChange("is_shared", e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_shared" className="text-sm text-gray-300">
            Share this report with other users in my organization
          </label>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
            canProceed
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-700 text-gray-500 cursor-not-allowed"
          }`}
        >
          Next: Configuration
        </button>
      </div>
    </div>
  );
};

export default BasicConfiguration;
